if (!customElements.get('yk-search-form')) {
  customElements.define('yk-search-form', class extends HTMLElement {
    connectedCallback() {
      this.lifecycle?.abort();
      this.predictive = false;
      this.composing = false;
      this.lifecycle = new AbortController();
      const { signal } = this.lifecycle;
      this.form = this.querySelector('form');
      this.input = this.form?.elements.q;
      this.clear = this.querySelector('[data-search-clear]');
      if (!this.input || !this.clear) return;
      this.clear.hidden = !this.input.value;
      this.input.addEventListener('compositionstart', () => {
        this.composing = true;
        this.close();
      }, { signal });
      this.input.addEventListener('compositionend', () => {
        this.composing = false;
        this.clear.hidden = !this.input.value;
        this.onInput();
      }, { signal });
      this.input.addEventListener('input', event => {
        this.clear.hidden = !this.input.value;
        if (this.composing || event.isComposing) return this.close();
        this.onInput();
      }, { signal });
      this.clear.addEventListener('click', () => {
        this.input.value = '';
        this.clear.hidden = true;
        this.close();
        this.input.focus();
      }, { signal });
      this.form.addEventListener('submit', event => {
        if (this.composing) return event.preventDefault();
        if (!this.input.value.trim()) {
          event.preventDefault();
          this.input.focus();
        }
      }, { signal });
      if (!this.hasAttribute('data-predictive')) return;
      this.popup = this.querySelector('[data-predictive-popup]');
      this.results = this.querySelector('[data-predictive-results]');
      this.message = this.querySelector('[data-predictive-message]');
      this.status = this.querySelector('[data-search-status]');
      this.all = this.querySelector('[data-search-all]');
      try { this.messages = JSON.parse(this.querySelector('[data-search-messages]').textContent); }
      catch { return; }
      try {
        const feature = document.getElementById('shopify-features');
        if (feature && JSON.parse(feature.textContent).predictiveSearch === false) return;
      } catch { return; }
      this.predictive = true;
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-haspopup', 'listbox');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.addEventListener('keydown', event => this.onKeydown(event), { signal });
      this.input.addEventListener('focus', () => {
        if (!this.suppressFocus && this.input.value.trim() && this.popup.hidden) this.onInput();
      }, { signal });
      this.input.addEventListener('click', () => {
        if (this.input.value.trim() && this.popup.hidden) this.onInput();
      }, { signal });
      this.addEventListener('keydown', event => {
        if (this.composing || event.isComposing || event.keyCode === 229) return;
        if (event.key === 'Escape' && !this.popup.hidden) {
          event.preventDefault();
          event.stopPropagation();
          this.close();
          this.suppressFocus = true;
          this.input.focus();
          this.suppressFocus = false;
        }
      }, { signal });
      this.querySelector('[data-search-close]')?.addEventListener('click', () => {
        this.close();
        this.suppressFocus = true;
        this.input.focus();
        this.suppressFocus = false;
      }, { signal });
      this.popup.addEventListener('pointerdown', () => { this.pointerInside = true; }, { signal });
      window.addEventListener('pointerup', () => {
        if (!this.pointerInside) return;
        clearTimeout(this.pointerReleaseTimer);
        this.pointerReleaseTimer = setTimeout(() => {
          this.pointerInside = false;
          if (!this.contains(document.activeElement)) this.close();
        }, 0);
      }, { signal });
      window.addEventListener('pointercancel', () => { this.pointerInside = false; }, { signal });
      this.addEventListener('focusout', event => {
        if (this.contains(event.relatedTarget)) return;
        queueMicrotask(() => {
          if (!this.pointerInside && !this.contains(document.activeElement)) this.close();
        });
      }, { signal });
      document.addEventListener('pointerdown', event => {
        if (!this.contains(event.target)) this.close();
      }, { signal });
      window.addEventListener('resize', () => this.schedulePosition(), { signal });
      window.addEventListener('scroll', () => this.schedulePosition(), { signal, passive: true });
      window.visualViewport?.addEventListener('resize', () => this.schedulePosition(), { signal });
      window.visualViewport?.addEventListener('scroll', () => this.schedulePosition(), { signal, passive: true });
      window.addEventListener('pagehide', () => this.close(), { signal });
      window.addEventListener('pageshow', event => {
        if (event.persisted) this.close();
        this.clear.hidden = !this.input.value;
      }, { signal });
    }
    disconnectedCallback() {
      this.close();
      this.lifecycle?.abort();
    }
    invalidate() {
      clearTimeout(this.timer);
      this.request?.abort();
      this.generation = (this.generation || 0) + 1;
    }
    close() {
      this.invalidate();
      clearTimeout(this.pointerReleaseTimer);
      this.pointerInside = false;
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      if (!this.popup) return;
      this.popup.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.input.removeAttribute('aria-controls');
      this.popup.removeAttribute('aria-busy');
      this.status.textContent = '';
      this.active = -1;
    }
    onInput() {
      if (!this.predictive || this.composing) return;
      this.invalidate();
      const query = this.input.value.trim();
      if (!query) return this.close();
      const generation = this.generation;
      const waitingList = document.createElement('div');
      waitingList.id = this.input.dataset.resultsId;
      waitingList.setAttribute('role', 'listbox');
      waitingList.setAttribute('aria-label', this.messages.suggestions);
      this.results.replaceChildren(waitingList);
      this.input.setAttribute('aria-controls', waitingList.id);
      this.input.removeAttribute('aria-activedescendant');
      this.active = -1;
      const allUrl = new URL(this.form.action, location.origin);
      allUrl.searchParams.set('q', query);
      allUrl.searchParams.set('type', 'product');
      this.all.href = allUrl.href;
      this.message.textContent = this.messages.loading;
      this.status.textContent = this.messages.loading;
      this.popup.setAttribute('aria-busy', 'true');
      this.popup.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.schedulePosition();
      this.timer = setTimeout(() => this.load(query, generation), 280);
    }
    schedulePosition() {
      if (this.frame || !this.popup || this.popup.hidden) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.position(); });
    }
    position() {
      if (!this.popup || this.popup.hidden) return;
      const viewport = window.visualViewport;
      const bottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
      const gap = parseFloat(getComputedStyle(this).getPropertyValue('--spacing-4')) || 0;
      const available = Math.max(0, bottom - this.getBoundingClientRect().bottom - gap);
      this.style.setProperty('--yk-search-popup-height', `${available}px`);
    }
    async load(query, generation) {
      this.request = new AbortController();
      const { signal } = this.request;
      const url = new URL(this.dataset.endpoint, location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('section_id', 'yk-predictive-search');
      url.searchParams.set('resources[type]', 'query,product,collection,page,article');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[limit_scope]', 'all');
      const preview = new URLSearchParams(location.search).get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      try {
        const response = await fetch(url, { signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('predictive response');
        const documentResult = new DOMParser().parseFromString(await response.text(), 'text/html');
        const result = documentResult.querySelector('[data-predictive-response]');
        const list = result?.querySelector('[data-predictive-list]');
        if (!list) throw new Error('predictive markup');
        if (signal.aborted || generation !== this.generation || query !== this.input.value.trim()) return;
        list.id = this.input.dataset.resultsId;
        const options = [...list.querySelectorAll('[data-predictive-option]')];
        options.forEach((option, index) => { option.id = `${list.id}-${index}`; });
        this.results.replaceChildren(document.importNode(list, true));
        this.input.setAttribute('aria-controls', list.id);
        const count = options.length;
        this.message.textContent = count ? '' : this.messages.empty;
        this.status.textContent = count ? (count === 1 ? this.messages.one : this.messages.many).replace('[count]', count) : this.messages.empty;
      } catch (error) {
        if (signal.aborted || generation !== this.generation || error.name === 'AbortError') return;
        this.results.querySelector('[role="listbox"]')?.replaceChildren();
        this.message.textContent = this.messages.error;
        this.status.textContent = this.messages.error;
      } finally {
        if (generation === this.generation) this.popup.removeAttribute('aria-busy');
      }
    }
    onKeydown(event) {
      if (this.composing || event.isComposing || event.keyCode === 229) return;
      if (event.key === 'Escape') return;
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Enter') return;
      const options = [...this.results.querySelectorAll('[data-predictive-option]')];
      if (event.key === 'Enter') {
        if (!this.popup.hidden && this.active >= 0 && options[this.active]) {
          event.preventDefault();
          options[this.active].click();
        }
        return;
      }
      if (this.popup.hidden) {
        if (this.input.value.trim()) { event.preventDefault(); this.onInput(); }
        return;
      }
      if (!options.length) return;
      event.preventDefault();
      this.active = event.key === 'ArrowDown' ? (this.active + 1) % options.length : (this.active <= 0 ? options.length - 1 : this.active - 1);
      options.forEach((option, index) => option.setAttribute('aria-selected', index === this.active ? 'true' : 'false'));
      const current = options[this.active];
      this.input.setAttribute('aria-activedescendant', current.id);
      const itemRect = current.getBoundingClientRect();
      const popupRect = this.popup.getBoundingClientRect();
      if (itemRect.bottom > popupRect.bottom) this.popup.scrollTop += itemRect.bottom - popupRect.bottom;
      else if (itemRect.top < popupRect.top) this.popup.scrollTop -= popupRect.top - itemRect.top;
    }
  });
}
